import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Loader2, Save, Webhook, Globe2, HelpCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import type { ThirdPartySettings, ThirdPartySettingsUpdateRequest } from '@/api/tenants/tenants.types';
import { useTenant } from '@/context/TenantContext';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';

interface ThirdPartySettingsCardProps {
    settings?: ThirdPartySettings;
    isLoading: boolean;
    onUpdateSettings: (settings: ThirdPartySettingsUpdateRequest) => Promise<void>;
}

export const ThirdPartySettingsCard = ({ 
    settings, 
    isLoading, 
    onUpdateSettings 
}: ThirdPartySettingsCardProps) => {
    const { branding } = useTenant();
    const [isUpdating, setIsUpdating] = useState(false);
    const [formData, setFormData] = useState<ThirdPartySettingsUpdateRequest>({
        webhooks_enabled: settings?.webhooks_enabled ?? false,
        webhook_url: settings?.webhook_url ?? ''
    });

    const validateUrl = (url: string) => {
        return !url || url.match(/^https:\/\/(dev\.)?(workflow\.(amira\.bot|voicebot\.studio))\/api\/v1\/webhooks\/[a-zA-Z0-9]+(\/(sync|async))?$/);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // Validate webhook URL if webhooks are enabled
        if (formData.webhooks_enabled && formData.webhook_url && !validateUrl(formData.webhook_url)) {
            return; // Don't submit if URL is invalid
        }
        
        setIsUpdating(true);
        try {
            await onUpdateSettings(formData);
        } catch (error) {
            // Error handling is done in parent component
        } finally {
            setIsUpdating(false);
        }
    };

    const handleInputChange = (field: keyof ThirdPartySettingsUpdateRequest, value: string | boolean) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const hasChanges = () => {
        return (
            formData.webhooks_enabled !== (settings?.webhooks_enabled ?? false) ||
            formData.webhook_url !== (settings?.webhook_url ?? '')
        );
    };

    const isFormValid = () => {
        // If webhooks are disabled, form is valid
        if (!formData.webhooks_enabled) return true;
        
        // If webhooks are enabled, URL must be provided and valid
        if (!formData.webhook_url) return false;
        
        return validateUrl(formData.webhook_url);
    };

    if (isLoading) {
        return (
            <Card className="shadow-sm">
                <CardHeader className="border-b bg-muted/20">
                    <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                            <Webhook className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex-1 space-y-1">
                            <CardTitle>Webhook Configuration</CardTitle>
                            <CardDescription>
                                Configure webhook notifications for your messaging service
                            </CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        <div className="animate-pulse">
                            <div className="h-4 bg-muted rounded w-1/4 mb-2"></div>
                            <div className="h-10 bg-muted rounded"></div>
                        </div>
                        <div className="animate-pulse">
                            <div className="h-4 bg-muted rounded w-1/3 mb-2"></div>
                            <div className="h-10 bg-muted rounded"></div>
                        </div>

                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
        >
            <Card className="shadow-sm">
                <CardHeader className="border-b bg-muted/20">
                    <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                            <Webhook className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex-1 space-y-1">
                            <CardTitle>Webhook Configuration</CardTitle>
                            <CardDescription>
                                Configure webhook notifications for your messaging service
                            </CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent className="space-y-6">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Webhooks Section */}
                        <div className="space-y-4">
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex gap-2">
                                    <Webhook className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                                    <div className="space-y-1">
                                        <div className="flex items-center gap-2">
                                            <Label className="text-sm font-medium">Enable Webhooks</Label>
                                            <TooltipProvider delayDuration={200}>
                                                <Tooltip>
                                                    <TooltipTrigger>
                                                        <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                                    </TooltipTrigger>
                                                    <TooltipContent className="max-w-[480px] p-3 space-y-2">
                                                        <p className="text-sm font-medium">Webhook Notifications</p>
                                                        <p className="text-sm text-muted-foreground">
                                                            Receive real-time notifications when messaging events occur. This system is integrated exclusively with Internal Workflows in a closed environment.
                                                        </p>
                                                        <div className="space-y-1">
                                                            <p className="text-xs font-medium">Valid URL Format:</p>
                                                            <code className="block text-xs bg-muted/50 px-2 py-1 rounded">
                                                                https://workflow.voicebot.studio/api/v1/webhooks/[ID]
                                                            </code>
                                                        </div>
                                                        <p className="text-xs text-muted-foreground">
                                                            External webhooks or third-party integrations are not supported.
                                                        </p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        </div>
                                        <p className="text-xs text-muted-foreground">Receive real-time notifications about messaging events</p>
                                    </div>
                                </div>
                                <Switch
                                    checked={formData.webhooks_enabled}
                                    onCheckedChange={(checked) => handleInputChange('webhooks_enabled', checked)}
                                />
                            </div>

                            {formData.webhooks_enabled && (
                                <div className="space-y-2">
                                    <Label className="flex items-center gap-2">
                                        <Globe2 className="h-4 w-4 text-primary" />
                                        Webhook URL
                                    </Label>
                                    <Input
                                        id="webhook-url"
                                        placeholder="https://workflow.voicebot.studio/api/v1/webhooks/[ID]"
                                        className={`h-9 font-mono ${
                                            formData.webhook_url && !validateUrl(formData.webhook_url) 
                                                ? 'border-destructive focus-visible:ring-destructive' 
                                                : ''
                                        }`}
                                        value={formData.webhook_url}
                                        onChange={(e) => handleInputChange('webhook_url', e.target.value)}
                                        autoComplete="off"
                                        type="url"
                                        spellCheck="false"
                                        required={formData.webhooks_enabled}
                                    />
                                    <p className="text-xs text-muted-foreground mt-1">
                                        This URL will receive webhook notifications from {branding?.company_name || 'Company'} and must be a valid Internal Workflow webhook URL.
                                    </p>
                                    {formData.webhook_url && !validateUrl(formData.webhook_url) && (
                                        <p className="text-xs text-destructive mt-1">
                                            Invalid URL format. This system only accepts Internal Workflow webhook URLs.
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>



                        {/* Submit Button */}
                        <div className="flex justify-end">
                            <Button 
                                type="submit" 
                                size="sm"
                                disabled={!hasChanges() || !isFormValid() || isUpdating}
                                className="gap-2"
                            >
                                {isUpdating ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Save className="h-4 w-4" />
                                        Save Changes
                                    </>
                                )}
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </motion.div>
    );
};
